import { supabase } from './supabase'

export interface BackupConfig {
  id: string
  enabled: boolean
  frequency: 'daily' | 'weekly' | 'monthly'
  retentionDays: number
  lastBackupAt: Date | null
  nextBackupAt: Date | null
  createdAt: Date
  updatedAt: Date
}

export interface BackupHistory {
  id: string
  versionNumber: number
  backupType: 'automatic' | 'manual'
  status: 'in_progress' | 'completed' | 'failed'
  fileSize: number | null
  tablesBackedUp: string[]
  createdByUserId: string | null
  createdByUserName: string | null
  errorMessage: string | null
  createdAt: Date
  restoredAt: Date | null
  restoredByUserId: string | null
  restoredByUserName: string | null
}

const CONFIG_KEY = 'savidna-backup-config'
const HISTORY_KEY = 'savidna-backup-history'

// Tablas a incluir en el backup
const BACKUP_TABLES = [
  'patients',
  'doctors',
  'appointments',
  'services',
  'specialties',
  'insurance',
  'clinical_histories',
  'contacts',
  'companies',
  'opportunities',
  'products',
  'product_sales',
  'follow_up_notes',
  'audit_logs',
  'users',
  'roles',
  'permissions',
]

function generateId(): string {
  return crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

function getDefaultConfig(): BackupConfig {
  return {
    id: generateId(),
    enabled: false,
    frequency: 'weekly',
    retentionDays: 30,
    lastBackupAt: null,
    nextBackupAt: null,
    createdAt: new Date(),
    updatedAt: new Date(),
  }
}

/**
 * Obtiene la configuración actual de backups desde localStorage
 */
export async function getBackupConfig(): Promise<BackupConfig> {
  try {
    const stored = localStorage.getItem(CONFIG_KEY)
    if (stored) {
      const data = JSON.parse(stored)
      return {
        ...data,
        lastBackupAt: data.lastBackupAt ? new Date(data.lastBackupAt) : null,
        nextBackupAt: data.nextBackupAt ? new Date(data.nextBackupAt) : null,
        createdAt: new Date(data.createdAt),
        updatedAt: new Date(data.updatedAt),
      }
    }
    const defaultConfig = getDefaultConfig()
    localStorage.setItem(CONFIG_KEY, JSON.stringify(defaultConfig))
    return defaultConfig
  } catch (error) {
    console.error('Error obteniendo configuración de backup:', error)
    const defaultConfig = getDefaultConfig()
    localStorage.setItem(CONFIG_KEY, JSON.stringify(defaultConfig))
    return defaultConfig
  }
}

/**
 * Actualiza la configuración de backups en localStorage
 */
export async function updateBackupConfig(
  config: Partial<BackupConfig>,
): Promise<boolean> {
  try {
    const current = await getBackupConfig()
    const updated: BackupConfig = {
      ...current,
      ...(config.enabled !== undefined ? { enabled: config.enabled } : {}),
      ...(config.frequency ? { frequency: config.frequency } : {}),
      ...(config.retentionDays !== undefined
        ? { retentionDays: config.retentionDays }
        : {}),
      ...(config.lastBackupAt !== undefined
        ? { lastBackupAt: config.lastBackupAt }
        : {}),
      ...(config.nextBackupAt !== undefined
        ? { nextBackupAt: config.nextBackupAt }
        : {}),
      updatedAt: new Date(),
    }
    localStorage.setItem(CONFIG_KEY, JSON.stringify(updated))
    return true
  } catch (error) {
    console.error('Error actualizando configuración de backup:', error)
    return false
  }
}

/**
 * Obtiene el historial de backups desde localStorage (solo metadatos)
 */
function getStoredHistory(): BackupHistory[] {
  try {
    const stored = localStorage.getItem(HISTORY_KEY)
    if (!stored) return []
    const data = JSON.parse(stored) as any[]
    return data.map((b) => ({
      ...b,
      createdAt: new Date(b.createdAt),
      restoredAt: b.restoredAt ? new Date(b.restoredAt) : null,
    }))
  } catch {
    return []
  }
}

function saveHistory(history: BackupHistory[]): void {
  // Strip any legacy backupData that might exist from old entries
  const metadataOnly = history.map(({ ...entry }) => {
    const clean: any = { ...entry }
    delete clean.backupData
    return clean
  })
  localStorage.setItem(HISTORY_KEY, JSON.stringify(metadataOnly))
}

/**
 * Descarga datos como archivo JSON
 */
function triggerDownload(data: any, versionNumber: number, date: Date): void {
  const dataStr = JSON.stringify(data, null, 2)
  const blob = new Blob([dataStr], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `savidna-backup-v${versionNumber}-${date.toISOString().split('T')[0]}.json`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Crea un backup manual o automático.
 * Los datos se descargan como archivo JSON inmediatamente.
 * Solo los metadatos se guardan en localStorage.
 */
export async function createBackup(
  userId: string,
  userName: string,
  type: 'automatic' | 'manual' = 'manual',
): Promise<{ success: boolean; backupId?: string; error?: string }> {
  try {
    const history = getStoredHistory()
    const maxVersion = history.reduce(
      (max, b) => Math.max(max, b.versionNumber),
      0,
    )
    const versionNumber = maxVersion + 1

    // Recopilar datos de todas las tablas desde Supabase
    const backupData: any = {}
    const tablesBackedUp: string[] = []
    let totalSize = 0

    for (const table of BACKUP_TABLES) {
      try {
        const { data, error } = await supabase.from(table).select('*')
        if (!error && data) {
          backupData[table] = data
          tablesBackedUp.push(table)
          totalSize += JSON.stringify(data).length
        }
      } catch (err) {
        console.warn(`No se pudo hacer backup de la tabla ${table}:`, err)
      }
    }

    const now = new Date()

    // Descargar el archivo inmediatamente
    triggerDownload(backupData, versionNumber, now)

    // Guardar solo metadatos en localStorage (sin backupData)
    const newEntry: BackupHistory = {
      id: generateId(),
      versionNumber,
      backupType: type,
      status: 'completed',
      fileSize: totalSize,
      tablesBackedUp,
      createdByUserId: userId,
      createdByUserName: userName,
      errorMessage: null,
      createdAt: now,
      restoredAt: null,
      restoredByUserId: null,
      restoredByUserName: null,
    }

    history.unshift(newEntry)
    saveHistory(history)

    // Actualizar última fecha de backup en config
    await updateBackupConfig({ lastBackupAt: now })

    return { success: true, backupId: newEntry.id }
  } catch (error: any) {
    console.error('Error creando backup:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Obtiene el historial de backups (solo metadatos)
 */
export async function getBackupHistory(): Promise<BackupHistory[]> {
  return getStoredHistory().sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )
}

/**
 * Restaura un backup desde un archivo JSON subido por el usuario.
 * Recibe los datos parseados del archivo.
 */
export async function restoreBackupFromFile(
  backupData: Record<string, any[]>,
  userId: string,
  userName: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    // Restaurar cada tabla en Supabase
    for (const [tableName, tableData] of Object.entries(backupData)) {
      if (!Array.isArray(tableData)) continue

      try {
        // Eliminar datos actuales
        await supabase
          .from(tableName)
          .delete()
          .neq('id', '00000000-0000-0000-0000-000000000000')

        // Insertar datos del backup
        if (tableData.length > 0) {
          await supabase.from(tableName).insert(tableData)
        }
      } catch (err) {
        console.warn(`Error restaurando tabla ${tableName}:`, err)
      }
    }

    // Registrar la restauración en el historial
    const history = getStoredHistory()
    const maxVersion = history.reduce(
      (max, b) => Math.max(max, b.versionNumber),
      0,
    )

    const restoreEntry: BackupHistory = {
      id: generateId(),
      versionNumber: maxVersion + 1,
      backupType: 'manual',
      status: 'completed',
      fileSize: JSON.stringify(backupData).length,
      tablesBackedUp: Object.keys(backupData),
      createdByUserId: userId,
      createdByUserName: userName,
      errorMessage: null,
      createdAt: new Date(),
      restoredAt: new Date(),
      restoredByUserId: userId,
      restoredByUserName: userName,
    }

    history.unshift(restoreEntry)
    saveHistory(history)

    return { success: true }
  } catch (error: any) {
    console.error('Error restaurando backup:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Elimina backups antiguos según la política de retención
 */
export async function cleanOldBackups(retentionDays: number): Promise<number> {
  try {
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - retentionDays)

    const history = getStoredHistory()
    const toKeep = history.filter(
      (b) => new Date(b.createdAt).getTime() >= cutoffDate.getTime(),
    )
    const deleted = history.length - toKeep.length
    saveHistory(toKeep)
    return deleted
  } catch (error) {
    console.error('Error limpiando backups antiguos:', error)
    return 0
  }
}

/**
 * Elimina un backup específico
 */
export async function deleteBackup(backupId: string): Promise<boolean> {
  try {
    const history = getStoredHistory()
    const filtered = history.filter((b) => b.id !== backupId)
    saveHistory(filtered)
    return true
  } catch (error) {
    console.error('Error eliminando backup:', error)
    return false
  }
}

/**
 * Formatea el tamaño del archivo
 */
export function formatFileSize(bytes: number | null): string {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`
}
